import { useState, useCallback, useRef, useEffect } from "react";
import { API_HOST } from "../constants";
import type { QueryStatus } from "../types";

interface ResultGroupItem {
  label: string;
  columns: string[];
  rows: unknown[][];
}

interface QueryResponse {
  rows: unknown[][];
  columns: string[];
  result_groups?: ResultGroupItem[];
  error_msg?: string;
  error_msg_full?: string; // полный текст ошибки для раскрытия
  status?: boolean; // для CheckResponse
  decision?: string; // для CheckResponse
  execution_time_ms?: number; // время выполнения в миллисекундах
  memory_usage_bytes?: number; // использование памяти в байтах
  read_bytes?: number; // прочитано байт
  read_rows?: number; // прочитано строк
  written_bytes?: number; // записано байт
  written_rows?: number; // записано строк
  result_bytes?: number; // размер результата в байтах
  result_rows?: number; // количество строк в результате
  cpu_time_us?: number; // CPU время в микросекундах
  trace?: string[]; // трейс-логи, если был запрошен флаг trace
}

interface UseQueryExecutionOptions {
  taskId: number;
  sql: string;
  onProgressSave?: (status: "success" | "error", code?: string, error_msg?: string) => Promise<void>;
}

const formatRows = (rows: unknown[][], columns: string[]) => {
  return rows.map((row) =>
    Object.fromEntries(columns.map((c, i) => [c, row[i]]))
  );
};

export const useQueryExecution = ({ taskId, sql, onProgressSave }: UseQueryExecutionOptions) => {
  const [status, setStatus] = useState<QueryStatus>("idle");
  const [errorMsg, setErrorMsg] = useState("");
  const [errorMsgFull, setErrorMsgFull] = useState("");
  const [result, setResult] = useState<Record<string, unknown>[]>([]);
  const [resultColumns, setResultColumns] = useState<string[]>([]);
  const [resultGroups, setResultGroups] = useState<Array<{ label: string; columns: string[]; rows: Record<string, unknown>[] }>>([]);
  const [executionTimeMs, setExecutionTimeMs] = useState<number | null>(null);
  const [memoryUsageBytes, setMemoryUsageBytes] = useState<number | null>(null);
  const [readBytes, setReadBytes] = useState<number | null>(null);
  const [readRows, setReadRows] = useState<number | null>(null);
  const [writtenBytes, setWrittenBytes] = useState<number | null>(null);
  const [writtenRows, setWrittenRows] = useState<number | null>(null);
  const [resultBytes, setResultBytes] = useState<number | null>(null);
  const [resultRows, setResultRows] = useState<number | null>(null);
  const [cpuTimeUs, setCpuTimeUs] = useState<number | null>(null);
  const [traceLogs, setTraceLogs] = useState<string[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const onProgressSaveRef = useRef(onProgressSave);
  const makeSessionId = () =>
    typeof crypto !== "undefined" && "randomUUID" in crypto ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`;

  // Обновляем ref при изменении onProgressSave
  useEffect(() => {
    onProgressSaveRef.current = onProgressSave;
  }, [onProgressSave]);

  // Очистка при размонтировании
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const executeQuery = useCallback(
    async (endpoint: string, check: boolean, debug: boolean = false) => {
      // Отменяем предыдущий запрос
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      abortControllerRef.current = new AbortController();
      setIsLoading(true);

      try {
        // Новая ClickHouse-сессия на каждый запуск запроса.
        // При этом мультизапросы через ';' выполняются в рамках одной сессии на бэкенде.
        const session_id = makeSessionId();

        const res = await fetch(endpoint, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ taskId, query: sql, trace: debug, session_id }),
          signal: abortControllerRef.current.signal,
        });

        if (!res.ok) throw new Error("Ошибка выполнения запроса");

        const data: QueryResponse = await res.json();

        // Сохраняем все метрики выполнения
        setExecutionTimeMs(data.execution_time_ms ?? null);
        setMemoryUsageBytes(data.memory_usage_bytes ?? null);
        setReadBytes(data.read_bytes ?? null);
        setReadRows(data.read_rows ?? null);
        setWrittenBytes(data.written_bytes ?? null);
        setWrittenRows(data.written_rows ?? null);
        setResultBytes(data.result_bytes ?? null);
        setResultRows(data.result_rows ?? null);
        setCpuTimeUs(data.cpu_time_us ?? null);
        
        // Сохраняем трейс-логи, если они есть
        setTraceLogs(data.trace ?? null);

        if (check) {
          // При проверке используем status из CheckResponse
          // Всегда устанавливаем результат, если есть данные, независимо от статуса проверки
          const formattedRows = formatRows(data.rows || [], data.columns || []);
          setResult(formattedRows);
          setResultColumns(data.columns || []);

          if (data.status === false) {
            // Проверка не прошла, но данные показываем
            const msg = data.error_msg || data.decision || "Ошибка проверки";
            setErrorMsg(msg);
            setErrorMsgFull((data as { error_msg_full?: string }).error_msg_full ?? "");
            setStatus("error");
            if (onProgressSaveRef.current) {
              await onProgressSaveRef.current("error", sql, msg);
            }
          } else {
            // Проверка прошла успешно
            setErrorMsg("");
            setErrorMsgFull("");
            setStatus("success");
            if (onProgressSaveRef.current) {
              await onProgressSaveRef.current("success", sql);
            }
          }
        } else {
          // При обычном запуске проверяем только error_msg
          if (data.error_msg) {
            setErrorMsg(data.error_msg);
            setErrorMsgFull(data.error_msg_full ?? "");
            setResult([]);
            setResultColumns([]);
            setResultGroups([]);
            // Сбрасываем все метрики при ошибке
            setExecutionTimeMs(null);
            setMemoryUsageBytes(null);
            setReadBytes(null);
            setReadRows(null);
            setWrittenBytes(null);
            setWrittenRows(null);
            setResultBytes(null);
            setResultRows(null);
            setCpuTimeUs(null);
            setTraceLogs(null);
            setStatus("error");
            setIsLoading(false);
            return;
          }
          setErrorMsg("");
          setErrorMsgFull("");
          if (data.result_groups && data.result_groups.length > 0) {
            const groups = data.result_groups.map((g) => ({
              label: g.label,
              columns: g.columns,
              rows: formatRows(g.rows || [], g.columns),
            }));
            setResultGroups(groups);
            const last = groups[groups.length - 1];
            setResult(last.rows);
            setResultColumns(last.columns);
          } else {
            setResultGroups([]);
            const formattedRows = formatRows(data.rows || [], data.columns || []);
            setResult(formattedRows);
            setResultColumns(data.columns || []);
          }
          setStatus("idle");
        }
      } catch (err) {
        if (err instanceof Error && err.name === "AbortError") {
          setIsLoading(false);
          return; // Запрос был отменен
        }
        console.error(err);
        setResult([]);
        setResultColumns([]);
        setResultGroups([]);
        // Сбрасываем все метрики при ошибке
        setExecutionTimeMs(null);
        setMemoryUsageBytes(null);
        setReadBytes(null);
        setReadRows(null);
        setWrittenBytes(null);
        setWrittenRows(null);
        setResultBytes(null);
        setResultRows(null);
        setCpuTimeUs(null);
        setTraceLogs(null);
        setStatus("error");
        setErrorMsg("Ошибка при выполнении запроса");
        setErrorMsgFull("");
        if (check && onProgressSaveRef.current) {
          await onProgressSaveRef.current("error", sql, "Ошибка при выполнении запроса");
        }
      } finally {
        setIsLoading(false);
      }
    },
    [taskId, sql]
  );

  const runQuery = useCallback(
    (check: boolean) => executeQuery(`${API_HOST}/api/run-query`, check, false),
    [executeQuery]
  );

  const runQueryDebug = useCallback(
    () => executeQuery(`${API_HOST}/api/run-query`, false, true),
    [executeQuery]
  );

  const checkQuery = useCallback(
    () => executeQuery(`${API_HOST}/api/check-query/${taskId}`, true),
    [executeQuery, taskId]
  );

  const resetResult = useCallback(() => {
    setResult([]);
    setResultColumns([]);
    setResultGroups([]);
    setErrorMsg("");
    setErrorMsgFull("");
    setExecutionTimeMs(null);
    setMemoryUsageBytes(null);
    setReadBytes(null);
    setReadRows(null);
    setWrittenBytes(null);
    setWrittenRows(null);
    setResultBytes(null);
    setResultRows(null);
    setCpuTimeUs(null);
    setTraceLogs(null);
    setStatus("idle");
  }, []);

  return {
    status,
    errorMsg,
    errorMsgFull,
    result,
    resultColumns,
    resultGroups,
    executionTimeMs,
    memoryUsageBytes,
    readBytes,
    readRows,
    writtenBytes,
    writtenRows,
    resultBytes,
    resultRows,
    cpuTimeUs,
    traceLogs,
    isLoading,
    runQuery,
    runQueryDebug,
    checkQuery,
    resetResult,
  };
};

